import {
  defaultGenericElement,
  getNextRetouchLayer,
  reversionElement,
  updateElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin, type BaseEventPayload } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { useEditorStore } from '../../stores/useEditorStore';
import { clipToMainImage } from '../../utils/canvas';
import { translateSvgPathWithSubpaths } from '../../utils/svg';
import { uid } from '../../utils/uid';
import {
  type GenericFreedrawElement,
  freedraw,
  type FreedrawState,
} from '../freedraw';
import { createFreedrawEvents } from '../freedraw/events';
import { getStrokePath } from '../freedraw/utils/path';
import { getFreedrawShape } from '../freedraw/utils/shape';
import { general } from '../general';

type Mask = {
  data: string;
  dx: number;
  dy: number;
};

export interface RetouchBrushElement extends GenericFreedrawElement {
  type: 'retouchBrush';
  mask: Mask | null;
  strokeCap: 'round' | 'square';
}

export interface RetouchBrushState extends FreedrawState<RetouchBrushElement> {
  limitToMask: boolean;
}

const state: RetouchBrushState = {
  newStrokeColor: 'rgba(60, 135, 247, 1)',
  newStrokeWidth: 16,
  booleanMode: 'union',
  limitToMask: false,
  newStrokeCap: 'round',
  lastPoint: null,
  lastDrawnElement: null,
};

const actions = {
  activate: () => {
    general.actions.activate(plugin.name, true, false);
    state.booleanMode = 'union';
  },
  setStrokeColor: (color: string) => {
    state.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    state.newStrokeWidth = width;
  },
  setStrokeCap: (cap: 'round' | 'square') => {
    state.newStrokeCap = cap;
  },
  setLimitToMask: (limit: boolean) => {
    state.limitToMask = limit;
  },
  updateColorForElement: (id: string, color: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(id) as RetouchBrushElement;

    updateElement<RetouchBrushElement>(elements, element, {
      strokeColor: color,
      ...reversionElement(element, ['canvas']),
    });

    renderStaticScene();
  },
};

const getRetouchBrushElementDefaults = ({
  elementState,
}: BaseEventPayload): Partial<RetouchBrushElement> => {
  const id = uid(5);

  const activeElementId = elementState.selectedElementIds;
  const activeElement = elementState.elements.get(activeElementId[0]);

  return {
    id,
    name: id,
    layer: getNextRetouchLayer(),

    isLocked: true,
    isDeletedFlag: false,
    isVisible: true,
    booleanMode: 'intersect',
    shape: {
      data: '',
      type: 'area',
    },
    mask:
      state.limitToMask && activeElement != null
        ? {
            data:
              activeElement.type !== 'boolean'
                ? translateSvgPathWithSubpaths(
                    activeElement.shape.data,
                    -activeElement.x,
                    -activeElement.y,
                  )
                : activeElement.shape.data,
            dx: activeElement.x,
            dy: activeElement.y,
          }
        : null,
    version: {
      bounds: `bounds-${id}-${uid(3)}`,
      canvas: `canvas-${id}-${uid(3)}`,
      path: `path-${id}-${uid(3)}`,
    },
    originX: 0,
    originY: 0,
    angle: 0,
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  };
};

const plugin: Plugin<RetouchBrushElement, typeof actions, typeof state> = {
  name: 'retouchBrush',
  cursor: 'crosshair',
  actions,
  state: state,
  events: {
    ...createFreedrawEvents<RetouchBrushElement>({
      toolName: 'retouchBrush',
      elementType: 'retouchBrush',
      state,
      getElementDefaults: getRetouchBrushElementDefaults,
    }),
    blockTouchPanning: true,
  },
  render: {
    ...freedraw.render,
    draw: ({ config, context, element, runtimeState, elementState }) => {
      if (!runtimeState.newElement && !element.points.length) return;

      if (element.renderedById != null) return;

      context.save();

      clipToMainImage(
        context,
        elementState.elements,
        element,
        element.strokeWidth / 2,
      );

      if (element.mask != null) {
        const clipPath = new Path2D(
          translateSvgPathWithSubpaths(
            element.mask.data,
            element.mask.dx - element.x + element.strokeWidth / 2,
            element.mask.dy - element.y + element.strokeWidth / 2,
          ),
        );
        context.clip(clipPath);
      }

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      context.strokeStyle = element.strokeColor;
      context.lineWidth = element.strokeWidth;
      context.lineCap = element.strokeCap ?? 'round';

      const path = getStrokePath(
        element.points,
        element.bezierHandles,
        element.strokeWidth / 2,
      );
      context.stroke(path);

      context.restore();
    },
    isRetouchTool: true,
  },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<RetouchBrushElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: false,
    bounds: freedraw.collision.bounds,
    calculateShape: () => ({ data: '', type: 'line' }),
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    resizedBounds: () => [0, 0, 0, 0, 0, 0],
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as RetouchBrushElement;

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const retouchBrush = plugin;
